<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    @save="onSave"
    :disable="!modifiedData.isFromManager"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div>
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          :disabled="!modifiedData.isFromManager"
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div>
        <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          :disabled="!modifiedData.isFromManager"
          v-model="modifiedData.code"
          color="base"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
      </div>
      <BaseSelectDropdown
        :items="dropdownTypes"
        :selected-item="selectedItem"
        :label="$t('salesperson.type')"
        clearable
        @update:selected="handleSelection"
        name="deliveryType"
        show-error-as-tooltip
        :disable="!modifiedData.isFromManager"
        :error="$v.type.required.$invalid ? $v.type.required.$message : ''" />

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="active"
        v-model="modifiedData.isActive"
        :disable="!modifiedData.isFromManager" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditDeliveryTypes } from '@/services/api/documents';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { enumToDropdownItems } from '@/utils/enums';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import { type DeliveryType, eDeliveryType } from '@/types/DeliveryTypes';
import { type SelectDropdownItem } from '@/types/Base';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    deliveryType: DeliveryType;
  }>(),
  {}
);

const modifiedData = ref<DeliveryType>(deepCopy<DeliveryType>(props.deliveryType));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.type = newData.eType ?? '';
    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.deliveryType);
  },
  { deep: true }
);

const dropdownTypes = ref(enumToDropdownItems(eDeliveryType));

const selectedItem = ref<SelectDropdownItem | null>(null);

function handleSelection(item: SelectDropdownItem) {
  selectedItem.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.eType = item ? +item.value : null;
  }
}

function onDismissChanges() {
  modifiedData.value = deepCopy<DeliveryType>(props.deliveryType);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditDeliveryTypes(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
}

onMounted(() => {
  if (modifiedData.value) {
    const selectedValue = dropdownTypes.value.find(
      (item) => item.value === modifiedData.value.eType
    );
    if (selectedValue) {
      selectedItem.value = selectedValue;
    }
    $v.value.$reset();
  }
});

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  type: modifiedData.value.eType ?? '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  type: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
